import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import modalStyles from 'components/Modal/modal.module.css';
import './transaction-filters-modal.css';

const defaultStyle = {
  margin: '0 auto',
  marginTop: '2%',
  maxWidth: '70%',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  overflowY: 'scroll',
  maxHeight: '85vh',
};

const TransactionFiltersModal = ({ isOpen, onClose, filters, onApplyFilters, onClearFilters }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [searchFieldType, setSearchFieldType] = useState('reference');

  React.useEffect(() => {
    setLocalFilters(filters);
    // Determine which search field has a value to set the dropdown
    const searchFields = ['reference', 'maskedPan', 'merchantId', 'merchantName', 'terminalId', 'RetrievalReferenceNumber'];
    const activeField = searchFields.find((field) => filters[field]);
    if (activeField) {
      setSearchFieldType(activeField);
    }
  }, [filters]);

  const handleFieldChange = (field, value) => {
    setLocalFilters((prev) => {
      const newFilters = {
        ...prev,
        [field]: value === '' ? undefined : value,
      };

      // If start date changes and is after end date, clear end date
      if (field === 'startDate' && value && newFilters.endDate && value > newFilters.endDate) {
        newFilters.endDate = undefined;
      }

      // If end date changes and is before start date, don't allow it
      if (field === 'endDate' && value && newFilters.startDate && value < newFilters.startDate) {
        return prev; // Don't update if end date is before start date
      }

      return newFilters;
    });
  };

  const handleCombinedSearchChange = (fieldType, value) => {
    // Clear all other search fields when switching or entering a value
    const searchFields = ['reference', 'maskedPan', 'merchantId', 'merchantName', 'terminalId', 'RetrievalReferenceNumber'];
    const newFilters = { ...localFilters };

    // Clear all search fields first
    searchFields.forEach((field) => {
      delete newFilters[field];
    });

    // Set the selected field if value exists
    if (value && value !== '') {
      newFilters[fieldType] = value;
    }

    setLocalFilters(newFilters);
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
    onClose();
  };

  const handleClear = () => {
    setLocalFilters({});
    setSearchFieldType('reference');
  };

  const statusOptions = [
    { name: 'Pending', value: '0' },
    { name: 'Success', value: '1' },
  ];

  const settledOptions = [
    { name: 'True', value: 'true' },
    { name: 'False', value: 'false' },
  ];

  const providerOptions = [{ name: 'Accelerex', value: 'accelerex' }, { name: 'Grupp', value: 'grupp' }];

  const transactionTypeOptions = [
    { name: 'Card', value: 'card' },
    { name: 'Transfer', value: 'transfer' },
  ];

  const searchFieldOptions = [
    { name: 'Reference', value: 'reference' },
    { name: 'Masked PAN', value: 'maskedPan' },
    { name: 'Merchant ID', value: 'merchantId' },
    { name: 'Merchant Name', value: 'merchantName' },
    { name: 'Terminal ID', value: 'terminalId' },
    { name: 'Retrieval Reference Number', value: 'RetrievalReferenceNumber' },
  ];

  return (
    <Modal open={isOpen} onClose={onClose} aria-labelledby='filter-modal-title' aria-describedby='filter-modal-description'>
      <Box sx={{ ...defaultStyle, width: '900px' }}>
        <Typography id='filter-modal-title' variant='h6' component='h2'>
          Filter Transactions
        </Typography>
        <div className={modalStyles.container}>
          <div className='transaction-filters-modal'>
            <div className='filters-grid'>
              {/* Combined Search Input */}
              <div className='filter-group combined-search-group'>
                <label>Search</label>
                <div className='combined-search-wrapper'>
                  <input
                    type='text'
                    className='combined-search-input'
                    value={localFilters[searchFieldType] || ''}
                    onChange={(e) => handleCombinedSearchChange(searchFieldType, e.target.value)}
                    placeholder={`Enter ${searchFieldOptions.find((opt) => opt.value === searchFieldType)?.name.toLowerCase()}`}
                  />
                  <select
                    className='combined-search-select'
                    value={searchFieldType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      const currentValue = localFilters[searchFieldType];

                      // Clear all search fields
                      const searchFields = ['reference', 'maskedPan', 'merchantId', 'merchantName', 'terminalId', 'RetrievalReferenceNumber'];
                      const newFilters = { ...localFilters };
                      searchFields.forEach((field) => {
                        delete newFilters[field];
                      });

                      // If there's a value in the current field, move it to the new field
                      if (currentValue) {
                        newFilters[newType] = currentValue;
                      }

                      setSearchFieldType(newType);
                      setLocalFilters(newFilters);
                    }}>
                    {searchFieldOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className='filter-group'>
                <label>Status</label>
                <select value={localFilters.status || ''} onChange={(e) => handleFieldChange('status', e.target.value)}>
                  <option value=''>Choose...</option>
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='filter-group'>
                <label>Settled</label>
                <select value={localFilters.settled || ''} onChange={(e) => handleFieldChange('settled', e.target.value)}>
                  <option value=''>Choose...</option>
                  {settledOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='filter-group'>
                <label>Provider</label>
                <select value={localFilters.provider || ''} onChange={(e) => handleFieldChange('provider', e.target.value)}>
                  <option value=''>Choose...</option>
                  {providerOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='filter-group'>
                <label>Transaction Type</label>
                <select value={localFilters.transactionType || ''} onChange={(e) => handleFieldChange('transactionType', e.target.value)}>
                  <option value=''>Choose...</option>
                  {transactionTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className='filter-group'>
                <label>Start Date</label>
                <input
                  type='date'
                  value={localFilters.startDate || ''}
                  onChange={(e) => handleFieldChange('startDate', e.target.value)}
                  max={localFilters.endDate || ''}
                />
              </div>

              <div className='filter-group'>
                <label>End Date</label>
                <input
                  type='date'
                  value={localFilters.endDate || ''}
                  onChange={(e) => handleFieldChange('endDate', e.target.value)}
                  min={localFilters.startDate || ''}
                />
              </div>

              <div className='filter-group'>
                <label>Min Amount</label>
                <input
                  type='number'
                  value={localFilters.minAmount || ''}
                  onChange={(e) => handleFieldChange('minAmount', e.target.value)}
                  placeholder='Enter minimum amount'
                />
              </div>

              <div className='filter-group'>
                <label>Max Amount</label>
                <input
                  type='number'
                  value={localFilters.maxAmount || ''}
                  onChange={(e) => handleFieldChange('maxAmount', e.target.value)}
                  placeholder='Enter maximum amount'
                />
              </div>
            </div>

            <div className='filters-modal-actions'>
              <button className='clear-filters-button' onClick={handleClear}>
                Clear Filters
              </button>
              <button className='apply-filters-button' onClick={handleApply}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default TransactionFiltersModal;
